import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { SecureImage } from "@/components/SecureImage";
import { FileUploadField } from "@/components/FileUploadField";
import { Loader2, QrCode, Save } from "lucide-react";
import { toast } from "sonner";

export default function AdminRepaymentSettings() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [s, setS] = useState({ instructions: "", enabled: false, late_penalty: "", upi_id: "", qr_file_id: null });

  const load = async () => {
    const { data } = await api.get("/admin/repayment-settings");
    setS({
      instructions: data.instructions || "", enabled: !!data.enabled,
      late_penalty: data.late_penalty ?? "", upi_id: data.upi_id || "", qr_file_id: data.qr_file_id || null,
    });
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/repayment-settings", {
        instructions: s.instructions, enabled: s.enabled,
        late_penalty: parseFloat(s.late_penalty) || 0, upi_id: s.upi_id, qr_file_id: s.qr_file_id,
      });
      toast.success("Repayment settings saved");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Repayment Settings</h1>
        <p className="text-muted-foreground text-sm">Control the payment QR, instructions and late penalty shown to customers.</p>
      </div>

      <Card className="p-5 sm:p-6 ip-card-shadow space-y-5">
        <div className="flex items-center justify-between">
          <div><p className="font-semibold">Enable online repayment</p><p className="text-xs text-muted-foreground">When off, customers cannot submit repayments.</p></div>
          <Switch checked={s.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} data-testid="repayment-enabled-switch" />
        </div>

        <div>
          <Label className="mb-2 block">Payment QR image</Label>
          <div className="flex items-start gap-4">
            {s.qr_file_id ? (
              <SecureImage fileId={s.qr_file_id} testId="settings-qr-image" className="h-36 w-36 object-contain rounded-lg border border-border bg-white" />
            ) : (
              <div className="h-36 w-36 grid place-items-center rounded-lg border border-dashed border-border text-muted-foreground"><QrCode className="h-8 w-8" /></div>
            )}
            <div className="flex-1">
              <FileUploadField label={s.qr_file_id ? "Replace QR image" : "Upload QR image"} purpose="repayment_qr" accept="image/*" testId="qr-upload" onUploaded={(id) => setS((p) => ({ ...p, qr_file_id: id }))} />
              <p className="text-xs text-muted-foreground mt-2">The latest active QR is automatically shown to customers.</p>
            </div>
          </div>
        </div>

        <div>
          <Label className="mb-1.5 block">UPI ID (optional)</Label>
          <Input data-testid="settings-upi-input" value={s.upi_id} onChange={(e) => setS({ ...s, upi_id: e.target.value })} placeholder="name@bank" />
        </div>

        <div>
          <Label className="mb-1.5 block">Payment instructions</Label>
          <Textarea data-testid="settings-instructions-input" value={s.instructions} onChange={(e) => setS({ ...s, instructions: e.target.value })} placeholder="e.g. Scan the QR, pay the outstanding amount, then upload your screenshot and UTR." rows={4} />
        </div>

        <div>
          <Label className="mb-1.5 block">Default late payment penalty (₹)</Label>
          <Input data-testid="settings-late-penalty-input" inputMode="numeric" value={s.late_penalty} onChange={(e) => setS({ ...s, late_penalty: e.target.value })} />
        </div>

        <Button onClick={save} disabled={busy} className="h-11" data-testid="settings-save-button">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save Settings
        </Button>
      </Card>
    </div>
  );
}
